import React, { useState, useMemo } from 'react';
import { Teacher, LearningObjective, Tugas, TugasType } from '../types.ts';
import { 
  INFORMATIKA_IX_CHAPTERS_FULL, 
  INFORMATIKA_IX_BOOK_URL, 
  ChapterDetails, 
  MaterialQuestion 
} from '../data/informatika9Data.ts';
import { INFORMATIKA_IX_QUESTIONS } from '../data/informatika9Questions.ts';
import { 
  BookOpen, 
  ExternalLink, 
  Search, 
  CheckCircle2, 
  Send, 
  Layers, 
  Sparkles, 
  FileText, 
  CheckSquare, 
  Square, 
  Filter, 
  X, 
  Clock, 
  Users, 
  Award, 
  ListOrdered,
  ChevronRight,
  HelpCircle,
  Copy,
  Check,
  Calendar,
  AlertCircle
} from 'lucide-react';

interface Props {
  activeUser: Teacher;
  objectives: LearningObjective[];
  onSaveTugas?: (tugas: Tugas) => void;
  onNavigateToTugas?: () => void;
}

const MaterialGeneratorView: React.FC<Props> = ({
  activeUser,
  objectives = [],
  onSaveTugas,
  onNavigateToTugas
}) => {
  // Navigation & View State
  const [activeTab, setActiveTab] = useState<'soal' | 'materi'>('soal');
  const [selectedChapterId, setSelectedChapterId] = useState<number>(1); // 1, 2, 3, 4, or 0 (all)
  const [selectedSectionCode, setSelectedSectionCode] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Selected questions for assignment
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  
  // Modal for publishing assignment to students
  const [isPublishModalOpen, setIsPublishModalOpen] = useState<boolean>(false);
  const [assignmentTitle, setAssignmentTitle] = useState<string>('');
  const [assignmentType, setAssignmentType] = useState<TugasType>(TugasType.TUGAS);

  // Available classes computation
  const availableClasses = useMemo(() => {
    const baseClasses = [
      '7A', '7B', '7C', '7D', '7E', '7F',
      '8A', '8B', '8C', '8D', '8E', '8F',
      '9A', '9B', '9C', '9D', '9E', '9F'
    ];
    const assigned = activeUser?.assignedClasses || [];
    const merged = Array.from(new Set([...assigned, ...baseClasses])).filter(Boolean);
    return merged.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }, [activeUser?.assignedClasses]);

  const [selectedClasses, setSelectedClasses] = useState<string[]>(
    activeUser?.assignedClasses?.length ? [...activeUser.assignedClasses] : ['9A', '9B', '9C', '9D', '9E', '9F']
  );

  const isAllClassesSelected = useMemo(() => {
    return availableClasses.length > 0 && availableClasses.every(cls => selectedClasses.includes(cls));
  }, [availableClasses, selectedClasses]);

  const handleToggleAllClasses = () => {
    if (isAllClassesSelected) {
      const fallback = activeUser?.assignedClasses?.length ? [activeUser.assignedClasses[0]] : ['9A'];
      setSelectedClasses(fallback);
    } else {
      setSelectedClasses([...availableClasses]);
    }
  };

  // Time & Schedule settings
  const [startTime, setStartTime] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [timeLimitOption, setTimeLimitOption] = useState<string>('60'); // default 60s per question
  const [customSeconds, setCustomSeconds] = useState<number>(60);
  const [selectedTpId, setSelectedTpId] = useState<string>('');
  const [isSummative, setIsSummative] = useState<boolean>(false);
  
  // Notification Toast State
  const [publishSuccessToast, setPublishSuccessToast] = useState<{
    show: boolean;
    title: string;
    questionCount: number;
  }>({ show: false, title: '', questionCount: 0 });

  const [copiedChapterId, setCopiedChapterId] = useState<number | null>(null);

  // Active chapter details
  const activeChapter = useMemo(() => {
    return INFORMATIKA_IX_CHAPTERS_FULL.find(c => c.id === selectedChapterId) || INFORMATIKA_IX_CHAPTERS_FULL[0];
  }, [selectedChapterId]);

  // Filtered Questions
  const filteredQuestions = useMemo(() => {
    return INFORMATIKA_IX_QUESTIONS.filter(q => {
      // Chapter filter
      if (selectedChapterId !== 0 && q.chapterId !== selectedChapterId) {
        return false;
      }
      // Section filter
      if (selectedSectionCode !== 'ALL' && q.sectionCode !== selectedSectionCode) {
        return false;
      }
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchText = q.text.toLowerCase().includes(query);
        const matchSection = q.sectionTitle.toLowerCase().includes(query);
        const matchOptions = q.options.some(opt => opt.toLowerCase().includes(query));
        return matchText || matchSection || matchOptions;
      }
      return true;
    });
  }, [selectedChapterId, selectedSectionCode, searchQuery]);

  // Selected questions objects
  const selectedQuestions = useMemo(() => {
    return INFORMATIKA_IX_QUESTIONS.filter(q => selectedQuestionIds.includes(q.id));
  }, [selectedQuestionIds]);

  // Handle single question toggle
  const handleToggleQuestion = (questionId: string) => {
    setSelectedQuestionIds(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  // Handle select all in current chapter
  const handleSelectAllInChapter = () => {
    const chapterQuestions = INFORMATIKA_IX_QUESTIONS.filter(q => q.chapterId === selectedChapterId);
    const chapterQuestionIds = chapterQuestions.map(q => q.id);
    
    // Check if all are already selected
    const allSelected = chapterQuestionIds.every(id => selectedQuestionIds.includes(id));
    if (allSelected) {
      // Unselect chapter questions
      setSelectedQuestionIds(prev => prev.filter(id => !chapterQuestionIds.includes(id)));
    } else {
      // Select all chapter questions
      setSelectedQuestionIds(prev => Array.from(new Set([...prev, ...chapterQuestionIds])));
    }
  };

  // Handle select all filtered questions
  const handleSelectAllFiltered = () => {
    const filteredIds = filteredQuestions.map(q => q.id);
    const allSelected = filteredIds.every(id => selectedQuestionIds.includes(id));
    if (allSelected) {
      setSelectedQuestionIds(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedQuestionIds(prev => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  // Handle open publish modal
  const handleOpenPublishModal = () => {
    if (selectedQuestionIds.length === 0) return;
    
    // Set default title based on selected chapter or general
    if (selectedChapterId !== 0) {
      const chapter = INFORMATIKA_IX_CHAPTERS_FULL.find(c => c.id === selectedChapterId);
      const prefix = chapter?.babNumber?.toLowerCase().includes('bab') ? `Tugas ${chapter?.babNumber}` : `Tugas Kokurikuler`;
      setAssignmentTitle(`${prefix}: ${chapter?.title}`);
    } else {
      setAssignmentTitle(`Tugas Terpadu Informatika Kelas IX (${selectedQuestionIds.length} Soal)`);
    }

    // Set default start time (now) and due date (7 days from now)
    const now = new Date();
    setStartTime(now.toISOString().slice(0, 16));

    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    setDueDate(nextWeek.toISOString().slice(0, 16));

    setIsPublishModalOpen(true);
  };

  // Handle publishing assignment to student task page
  const handlePublishAssignment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!assignmentTitle.trim() || selectedQuestionIds.length === 0) return;

    if (!onSaveTugas) {
      alert("Fungsi penyimpan tugas tidak tersedia.");
      return;
    }

    if (selectedClasses.length === 0) {
      alert("Harap pilih setidaknya satu Target Kelas Siswa.");
      return;
    }

    const pointsPerQuestion = Math.max(1, Math.round(100 / selectedQuestions.length));
    
    // Determine effective time limit per question in seconds
    let effectiveTimeLimit: number | undefined = undefined;
    if (timeLimitOption === 'custom') {
      effectiveTimeLimit = customSeconds > 0 ? customSeconds : undefined;
    } else {
      const parsedOption = Number(timeLimitOption);
      effectiveTimeLimit = parsedOption > 0 ? parsedOption : undefined;
    }

    const newTugas: Tugas = {
      id: `tugas-inf9-${Date.now()}`,
      title: assignmentTitle,
      description: `Tugas Pilihan Ganda dari Materi Informatika Kelas IX (${selectedQuestions.length} Soal). Silakan kerjakan dengan cermat.`,
      type: assignmentType,
      teacherId: activeUser.id || 'guru-1',
      subject: activeUser.subject || 'Informatika',
      class: selectedClasses[0] || '9A',
      targetClasses: selectedClasses,
      questions: selectedQuestions.map((q, idx) => ({
        id: `q-${idx + 1}-${Date.now()}`,
        text: q.text,
        type: 'multiple-choice',
        options: q.options,
        correctAnswerIndex: q.correctAnswerIndex,
        points: pointsPerQuestion,
        timeLimit: effectiveTimeLimit
      })),
      createdAt: new Date().toISOString(),
      startTime: startTime ? new Date(startTime).toISOString() : undefined,
      endTime: dueDate ? new Date(dueDate).toISOString() : undefined,
      dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
      isActive: true,
      isSummative: isSummative,
      tpId: selectedTpId || undefined
    };

    onSaveTugas(newTugas);

    // Close modal and show success toast
    setIsPublishModalOpen(false);
    setPublishSuccessToast({
      show: true,
      title: assignmentTitle,
      questionCount: selectedQuestions.length
    });

    // Reset selection after sending
    setSelectedQuestionIds([]);
  };

  // Handle copy chapter summary
  const handleCopyChapterSummary = (chapter: ChapterDetails) => {
    const text = `
=== ${chapter.babNumber}: ${chapter.title} ===
Elemen: ${chapter.element} (${chapter.elementCode})
Halaman Buku Teks: Mulai Hal. ${chapter.pageStart}

RINGKASAN CAPAIAN PEMBELAJARAN:
${chapter.summary}

${chapter.materialSummary ? `POIN KONSEP INTI MATERI:\n${chapter.materialSummary.map(m => `• ${m}`).join('\n')}\n\n` : ''}SUB-BAB MATERI & RANGKUMAN:
${chapter.sections.map(s => `[${s.code}] ${s.title} (Hal. ${s.page})\n${s.materialContent ? `   Ringkasan: ${s.materialContent}\n` : ''}`).join('\n')}
AKTIVITAS LKPD / PRAKTIK:
${chapter.lkpd}

Buku Teks Resmi (PDF): ${INFORMATIKA_IX_BOOK_URL}
    `.trim();

    navigator.clipboard.writeText(text);
    setCopiedChapterId(chapter.id);
    setTimeout(() => setCopiedChapterId(null), 2500);
  };

  return (
    <div className="space-y-6 pb-20">
      {/* Header Banner */}
      <div className="p-6 md:p-8 bg-gradient-to-br from-indigo-900 via-indigo-800 to-slate-900 rounded-3xl text-white shadow-xl relative overflow-hidden border border-indigo-700/50">
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-2 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-500/20 border border-indigo-400/30 rounded-full text-indigo-200 text-xs font-bold uppercase tracking-wider">
              <Sparkles size={14} className="text-amber-300" />
              <span>Modul Pengolahan Materi Informatika IX (Kurikulum Merdeka Rev)</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight">
              Bank Soal & Pengolahan Bab Informatika Kelas IX
            </h1>
            <p className="text-indigo-200 text-sm leading-relaxed">
              Disesuaikan secara persis dengan struktur Bab I - Bab IV Buku Teks Resmi Kemendikbudristek.
              Tersedia <strong className="text-amber-300">{INFORMATIKA_IX_QUESTIONS.length} Soal Pilihan Ganda</strong> yang dapat dipilih guru dan dikirimkan langsung ke Halaman Tugas Siswa.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <a
              href={INFORMATIKA_IX_BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-3 bg-white/10 hover:bg-white/20 text-white rounded-2xl text-xs font-bold transition-all border border-white/20 flex items-center gap-2 backdrop-blur-md"
            >
              <FileText size={16} className="text-cyan-300" />
              <span>Buka PDF Buku Teks</span>
              <ExternalLink size={14} className="opacity-70" />
            </a>

            {selectedQuestionIds.length > 0 && (
              <button
                onClick={handleOpenPublishModal}
                className="px-6 py-3 bg-amber-400 hover:bg-amber-300 text-slate-900 rounded-2xl text-xs font-black transition-all shadow-lg shadow-amber-400/20 flex items-center gap-2 animate-bounce"
              >
                <Send size={16} />
                <span>Kirim ke Tugas Siswa ({selectedQuestionIds.length})</span>
              </button>
            )}
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="mt-8 flex items-center gap-2 border-b border-indigo-700/50 pt-2">
          <button
            onClick={() => setActiveTab('soal')}
            className={`px-5 py-3 rounded-t-2xl text-xs font-black transition-all flex items-center gap-2 border-b-2 ${
              activeTab === 'soal'
                ? 'bg-white text-indigo-950 border-amber-400 font-extrabold shadow-sm'
                : 'text-indigo-200 hover:text-white border-transparent hover:bg-white/5'
            }`}
          >
            <HelpCircle size={16} />
            <span>Bank Soal Pilihan Ganda ({INFORMATIKA_IX_QUESTIONS.length} Soal)</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-400/20 text-amber-300 border border-amber-400/30 font-bold">
              Terlengkap
            </span>
          </button>

          <button
            onClick={() => setActiveTab('materi')}
            className={`px-5 py-3 rounded-t-2xl text-xs font-black transition-all flex items-center gap-2 border-b-2 ${
              activeTab === 'materi'
                ? 'bg-white text-indigo-950 border-amber-400 font-extrabold shadow-sm'
                : 'text-indigo-200 hover:text-white border-transparent hover:bg-white/5'
            }`}
          >
            <BookOpen size={16} />
            <span>Modul & Struktur Materi Bab I - V (Lengkap)</span>
          </button>
        </div>
      </div>

      {/* SUCCESS TOAST BANNER */}
      {publishSuccessToast.show && (
        <div className="p-4 bg-emerald-500 text-white rounded-2xl shadow-xl flex items-center justify-between gap-4 animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center gap-3">
            <CheckCircle2 size={24} className="shrink-0 text-white" />
            <div>
              <p className="font-bold text-sm">
                Berhasil Memublikasikan Tugas ke Halaman Siswa!
              </p>
              <p className="text-xs text-emerald-100">
                Tugas &ldquo;{publishSuccessToast.title}&rdquo; ({publishSuccessToast.questionCount} Soal) telah dikirimkan ke siswa.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onNavigateToTugas && (
              <button
                onClick={onNavigateToTugas}
                className="px-4 py-2 bg-white text-emerald-900 rounded-xl text-xs font-black hover:bg-emerald-50 transition-all flex items-center gap-1.5"
              >
                <span>Lihat di Halaman Tugas</span>
                <ChevronRight size={14} />
              </button>
            )}
            <button
              onClick={() => setPublishSuccessToast({ show: false, title: '', questionCount: 0 })}
              className="p-1.5 hover:bg-white/20 rounded-lg text-white"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* FLOATING ACTION BAR FOR SELECTED QUESTIONS */}
      {selectedQuestionIds.length > 0 && (
        <div className="sticky top-4 z-30 p-4 bg-slate-900 text-white rounded-2xl shadow-2xl border border-indigo-500/30 flex items-center justify-between gap-4 backdrop-blur-xl animate-in fade-in slide-in-from-top-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-400 text-slate-950 font-black text-sm flex items-center justify-center shrink-0">
              {selectedQuestionIds.length}
            </div>
            <div>
              <p className="font-bold text-sm text-white">Soal Dipilih untuk Diterbitkan</p>
              <p className="text-xs text-slate-400">
                Siap dikirimkan ke Halaman Tugas Siswa
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedQuestionIds([])}
              className="px-3 py-2 text-xs text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl transition-all"
            >
              Batalkan Pilihan
            </button>
            <button
              onClick={handleOpenPublishModal}
              className="px-5 py-2.5 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black rounded-xl text-xs transition-all shadow-md shadow-amber-400/20 flex items-center gap-2"
            >
              <Send size={15} />
              <span>Kirimkan Tugas Sekarang</span>
            </button>
          </div>
        </div>
      )}

      {/* CHAPTER SELECTOR CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {INFORMATIKA_IX_CHAPTERS_FULL.map(chapter => {
          const isSelected = selectedChapterId === chapter.id;
          const chapterQuestionCount = INFORMATIKA_IX_QUESTIONS.filter(q => q.chapterId === chapter.id).length;
          const selectedInChapterCount = INFORMATIKA_IX_QUESTIONS.filter(
            q => q.chapterId === chapter.id && selectedQuestionIds.includes(q.id)
          ).length;

          return (
            <button
              key={chapter.id}
              onClick={() => {
                setSelectedChapterId(chapter.id);
                setSelectedSectionCode('ALL');
              }}
              className={`p-5 rounded-3xl text-left transition-all border relative flex flex-col justify-between gap-4 ${
                isSelected
                  ? 'bg-white dark:bg-slate-900 border-indigo-600 shadow-xl ring-2 ring-indigo-500/20'
                  : 'bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-800 hover:border-indigo-300 hover:shadow-md'
              }`}
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black border ${chapter.badgeColor}`}>
                    {chapter.babNumber}
                  </span>
                  <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500">
                    Mulai Hal. {chapter.pageStart}
                  </span>
                </div>

                <h3 className="text-sm font-black text-slate-800 dark:text-white leading-tight line-clamp-2">
                  {chapter.title}
                </h3>
              </div>

              <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2 text-xs">
                <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1">
                  <HelpCircle size={13} className="text-indigo-500" />
                  <span>{chapterQuestionCount} Soal PG</span>
                </span>

                {selectedInChapterCount > 0 ? (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                    {selectedInChapterCount} Dipilih
                  </span>
                ) : (
                  <span className="text-[10px] text-indigo-600 font-bold hover:underline">
                    Lihat Bab 
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* TAB 1: BANK SOAL PILIHAN GANDA */}
      {activeTab === 'soal' && (
        <div className="space-y-6">
          {/* Filter & Search Bar */}
          <div className="p-5 bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              {/* Search input */}
              <div className="relative flex-1">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Cari kata kunci soal, topik, atau konsep..."
                  className="w-full pl-11 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs focus:ring-2 focus:ring-indigo-500 outline-none transition-all dark:text-white"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              {/* Quick Batch Actions */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handleSelectAllInChapter}
                  className="px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5"
                >
                  <CheckSquare size={14} />
                  <span>Pilih Semua {INFORMATIKA_IX_QUESTIONS.filter(q => q.chapterId === selectedChapterId).length} Soal Bab Ini</span>
                </button>

                {filteredQuestions.length !== INFORMATIKA_IX_QUESTIONS.filter(q => q.chapterId === selectedChapterId).length && (
                  <button
                    onClick={handleSelectAllFiltered}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition-all"
                  >
                    Pilih Terfilter ({filteredQuestions.length})
                  </button>
                )}
              </div>
            </div>

            {/* Sub-section Filter Pills */}
            <div className="pt-2 flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
              <span className="text-[11px] font-bold text-slate-400 shrink-0 flex items-center gap-1">
                <Filter size={12} />
                <span>Sub-Bab:</span>
              </span>

              <button
                onClick={() => setSelectedSectionCode('ALL')}
                className={`px-3 py-1 rounded-xl text-xs font-bold shrink-0 transition-all ${
                  selectedSectionCode === 'ALL'
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                }`}
              >
                Semua Sub-Bab ({INFORMATIKA_IX_QUESTIONS.filter(q => q.chapterId === selectedChapterId).length})
              </button>

              {activeChapter.sections.map(sec => {
                const countInSec = INFORMATIKA_IX_QUESTIONS.filter(q => q.chapterId === selectedChapterId && q.sectionCode === sec.code).length;
                return (
                  <button
                    key={sec.code}
                    onClick={() => setSelectedSectionCode(sec.code)}
                    className={`px-3 py-1 rounded-xl text-xs font-bold shrink-0 transition-all ${
                      selectedSectionCode === sec.code
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                    }`}
                  >
                    Sub-Bab {sec.code}: {sec.title.length > 20 ? sec.title.substring(0, 20) + '...' : sec.title} ({countInSec} Soal)
                  </button>
                );
              })}
            </div>
          </div>

          {/* QUESTIONS LIST */}
          <div className="space-y-4">
            <div className="flex items-center justify-between text-xs text-slate-500 px-1">
              <p>
                Menampilkan <strong className="text-slate-800 dark:text-white">{filteredQuestions.length} Soal</strong> dari {activeChapter.babNumber}
              </p>
              <p>
                {selectedQuestionIds.length} Soal dipilih dari seluruh bab
              </p>
            </div>

            {filteredQuestions.length === 0 ? (
              <div className="p-12 text-center bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 space-y-3">
                <Search size={32} className="mx-auto text-slate-400" />
                <p className="text-sm font-bold text-slate-700 dark:text-slate-300">
                  Tidak ada soal yang cocok dengan pencarian &ldquo;{searchQuery}&rdquo;
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedSectionCode('ALL');
                  }}
                  className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-xs font-bold hover:bg-indigo-100"
                >
                  Reset Filter
                </button>
              </div>
            ) : (
              filteredQuestions.map((q, idx) => {
                const isSelected = selectedQuestionIds.includes(q.id);

                return (
                  <div
                    key={q.id}
                    className={`p-5 md:p-6 rounded-3xl border transition-all space-y-4 ${
                      isSelected
                        ? 'bg-indigo-50/50 dark:bg-indigo-950/20 border-indigo-400 shadow-md ring-1 ring-indigo-400/30'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300'
                    }`}
                  >
                    {/* Question Header */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleToggleQuestion(q.id)}
                          className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all shrink-0 ${
                            isSelected
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-400 hover:border-indigo-400 border border-slate-300 dark:border-slate-700'
                          }`}
                        >
                          {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                        </button>

                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-2.5 py-0.5 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 text-[10px] font-black">
                            {INFORMATIKA_IX_CHAPTERS_FULL.find(c => c.id === q.chapterId)?.babNumber}
                          </span>
                          <span className="px-2.5 py-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-bold">
                            Sub {q.sectionCode}: {q.sectionTitle}
                          </span>
                        </div>
                      </div>

                      <span className="text-xs font-bold text-slate-400 shrink-0">
                        Soal #{idx + 1}
                      </span>
                    </div>

                    {/* Question Text */}
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-relaxed pl-9">
                      {q.text}
                    </p>

                    {/* Options Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-9">
                      {q.options.map((opt, oIdx) => {
                        const isCorrect = oIdx === q.correctAnswerIndex;
                        return (
                          <div
                            key={oIdx}
                            className={`p-3 rounded-2xl border text-xs flex items-center justify-between gap-3 ${
                              isCorrect
                                ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 text-emerald-900 dark:text-emerald-200 font-bold'
                                : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            <div className="flex items-center gap-2.5">
                              <span className={`w-6 h-6 rounded-lg text-[10px] font-black flex items-center justify-center shrink-0 ${
                                isCorrect 
                                  ? 'bg-emerald-600 text-white' 
                                  : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600'
                              }`}>
                                {String.fromCharCode(65 + oIdx)}
                              </span>
                              <span>{opt}</span>
                            </div>
                            {isCorrect && (
                              <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 shrink-0 uppercase tracking-wider">
                                [ Kunci ]
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Explanation Box */}
                    <div className="pl-9 pt-1">
                      <div className="p-3 bg-indigo-50/50 dark:bg-slate-800/80 rounded-2xl border border-indigo-100 dark:border-slate-700/60 text-xs text-slate-600 dark:text-slate-300 space-y-1">
                        <span className="font-bold text-indigo-700 dark:text-indigo-400 text-[10px] uppercase tracking-wider block">
                          💡 Pembahasan / Keterangan Pembelajaran:
                        </span>
                        <p>{q.explanation}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* TAB 2: KURIKULUM & DAFTAR ISI BAB */}
      {activeTab === 'materi' && (
        <div className="space-y-6">
          <div className="p-6 bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4">
              <div>
                <span className={`px-3 py-1 rounded-xl text-xs font-black border ${activeChapter.badgeColor}`}>
                  {activeChapter.babNumber}
                </span>
                <h2 className="text-xl font-black text-slate-800 dark:text-white mt-2">
                  {activeChapter.title}
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Elemen Pembelajaran: <strong>{activeChapter.element} ({activeChapter.elementCode})</strong>
                </p>
              </div>

              <button
                onClick={() => handleCopyChapterSummary(activeChapter)}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-2xl text-xs font-bold transition-all flex items-center gap-2 self-start sm:self-auto"
              >
                {copiedChapterId === activeChapter.id ? (
                  <>
                    <Check size={16} className="text-emerald-500" />
                    <span>Modul Tersalin!</span>
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    <span>Salin Ringkasan Modul</span>
                  </>
                )}
              </button>
            </div>

            {/* Summary Box */}
            <div className="p-5 bg-indigo-50/50 dark:bg-indigo-950/20 rounded-2xl border border-indigo-100 dark:border-indigo-900/30 space-y-2">
              <h4 className="text-xs font-black text-indigo-900 dark:text-indigo-300 uppercase tracking-wider flex items-center gap-2">
                <Sparkles size={15} className="text-amber-500" />
                <span>Ringkasan Capaian Pembelajaran Bab</span>
              </h4>
              <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed font-medium">
                {activeChapter.summary}
              </p>
            </div>

            {/* Core Concepts Bullet Points */}
            {activeChapter.materialSummary && activeChapter.materialSummary.length > 0 && (
              <div className="p-5 bg-slate-900 text-slate-100 rounded-2xl border border-slate-800 space-y-3">
                <h4 className="text-xs font-black text-amber-400 uppercase tracking-wider flex items-center gap-2">
                  <BookOpen size={15} />
                  <span>📌 Poin Konsep Inti Pembelajaran</span>
                </h4>
                <ul className="space-y-2 text-xs">
                  {activeChapter.materialSummary.map((point, pIdx) => (
                    <li key={pIdx} className="flex items-start gap-2.5 text-slate-300 leading-relaxed">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Sections Breakdown according to exact Table of Contents */}
            <div className="space-y-4">
              <h4 className="text-xs font-black text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <FileText size={15} />
                <span>📖 Rincian Materi, Sub-Bab & Penjelasan Konsep</span>
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activeChapter.sections.map((sec, sIdx) => (
                  <div
                    key={sIdx}
                    className="p-5 bg-slate-50 dark:bg-slate-800/60 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-3 flex flex-col justify-between"
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="w-8 h-8 rounded-xl bg-indigo-600 text-white font-black text-xs flex items-center justify-center shrink-0">
                          {sec.code}
                        </span>
                        <span className="text-xs font-bold text-slate-500 bg-white dark:bg-slate-700 px-3 py-1 rounded-xl border border-slate-200 dark:border-slate-600">
                          Halaman {sec.page}
                        </span>
                      </div>

                      <h5 className="text-sm font-black text-slate-800 dark:text-white">
                        {sec.title}
                      </h5>

                      {/* Material Detailed Content Explanation */}
                      {sec.materialContent && (
                        <div className="p-4 bg-white dark:bg-slate-900/90 rounded-xl border border-slate-200 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-300 leading-relaxed space-y-2 shadow-sm">
                          <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest block pb-1 border-b border-slate-100 dark:border-slate-800">
                            📘 Rangkuman & Penjelasan Konsep:
                          </span>
                          <div className="space-y-2.5 pt-1">
                            {sec.materialContent.split('\n\n').map((para, pIdx) => (
                              <p key={pIdx} className="whitespace-pre-line leading-relaxed font-normal">
                                {para}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Subsections list if available */}
                      {sec.subsections && sec.subsections.length > 0 && (
                        <div className="pt-2 border-t border-slate-200 dark:border-slate-700/60 space-y-1.5">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">
                            Topik Bahasan Sub-Bab:
                          </span>
                          {sec.subsections.map(sub => (
                            <div key={sub.num} className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-300 pl-2">
                              <span>{sub.num}. {sub.title}</span>
                              <span className="text-[10px] text-slate-400 font-bold">Hal. {sub.page}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* LKPD / Activity Box */}
            <div className="p-5 bg-amber-50 dark:bg-amber-950/20 rounded-2xl border border-amber-200 dark:border-amber-900/30 space-y-2">
              <h4 className="text-xs font-black text-amber-900 dark:text-amber-300 uppercase tracking-wider flex items-center gap-2">
                <Award size={16} />
                <span>Aktivitas Praktik / LKPD Siswa</span>
              </h4>
              <p className="text-xs text-amber-950 dark:text-amber-200 leading-relaxed font-medium">
                {activeChapter.lkpd}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: PUBLISH ASSIGNMENT TO STUDENT TASK PAGE */}
      {isPublishModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-2xl w-full border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden flex flex-col max-h-[90vh] my-auto animate-in zoom-in-95">
            {/* Modal Header */}
            <div className="p-5 sm:p-6 bg-gradient-to-r from-indigo-900 to-slate-900 text-white flex items-center justify-between gap-4 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-amber-400 text-slate-950 font-black flex items-center justify-center shrink-0">
                  <Send size={20} />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-black text-white">Kirimkan Soal ke Halaman Tugas Siswa</h3>
                  <p className="text-xs text-indigo-200">
                    Menyusun {selectedQuestions.length} Soal Pilihan Ganda ke daftar tugas terstruktur.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsPublishModalOpen(false)}
                className="p-2 hover:bg-white/10 rounded-xl text-white transition-all shrink-0"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body / Form */}
            <form onSubmit={handlePublishAssignment} className="flex flex-col flex-1 overflow-hidden min-h-0">
              <div className="p-5 sm:p-6 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
                {/* Assignment Title */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    Judul Tugas
                  </label>
                  <input
                    type="text"
                    required
                    value={assignmentTitle}
                    onChange={e => setAssignmentTitle(e.target.value)}
                    placeholder="Contoh: Tugas Bab I: Berpikir Komputasional dalam Analisis Data"
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Assignment Type */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                      Kategori Tugas
                    </label>
                    <select
                      value={assignmentType}
                      onChange={e => setAssignmentType(e.target.value as TugasType)}
                      className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white"
                    >
                      <option value={TugasType.TUGAS}>Tugas Reguler</option>
                      <option value={TugasType.TUGAS_NILAI_TAMBAH}>Tugas Nilai Tambah (Ekstra)</option>
                    </select>
                  </div>

                  {/* Target TP Objective */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                      Hubungkan dengan Tujuan Pembelajaran (TP)
                    </label>
                    <select
                      value={selectedTpId}
                      onChange={e => setSelectedTpId(e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white"
                    >
                      <option value="">-- Tanpa TP Terhubung --</option>
                      {objectives.map(obj => (
                        <option key={obj.id} value={obj.id}>
                          {obj.code}: {obj.description.slice(0, 60)}...
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Target Classes Section */}
                <div className="space-y-2 p-4 bg-slate-50 dark:bg-slate-800/60 rounded-2xl border border-slate-200 dark:border-slate-700">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 dark:border-slate-700/80 pb-2.5">
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-indigo-600 dark:text-indigo-400" />
                      <div>
                        <span className="text-xs font-black text-slate-800 dark:text-white block">
                          Target Kelas Siswa
                        </span>
                        <span className="text-[11px] text-slate-500 font-medium">
                          Terpilih: <strong className="text-indigo-600 dark:text-indigo-400 font-bold">{selectedClasses.length}</strong> dari {availableClasses.length} Kelas
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleToggleAllClasses}
                      className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 border shadow-sm ${
                        isAllClassesSelected
                          ? 'bg-amber-400 text-slate-950 border-amber-400 hover:bg-amber-300'
                          : 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                      }`}
                    >
                      {isAllClassesSelected ? <CheckCircle2 size={14} /> : <CheckSquare size={14} />}
                      <span>{isAllClassesSelected ? 'Semua Kelas Terpilih' : 'Pilih Semua Kelas'}</span>
                    </button>
                  </div>

                  <div className="pt-2 flex flex-wrap gap-2 max-h-44 overflow-y-auto pr-1 custom-scrollbar">
                    {availableClasses.map(cls => {
                      const isChecked = selectedClasses.includes(cls);
                      return (
                        <button
                          type="button"
                          key={cls}
                          onClick={() => {
                            setSelectedClasses(prev => 
                              prev.includes(cls) 
                                ? (prev.length > 1 ? prev.filter(c => c !== cls) : prev) 
                                : [...prev, cls]
                            );
                          }}
                          className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 border ${
                            isChecked
                              ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                              : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-indigo-300'
                          }`}
                        >
                          {isChecked && <Check size={13} className="stroke-[3]" />}
                          <span>Kelas {cls}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Schedule & Access Window */}
                <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-800/60 rounded-2xl border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-700/80 pb-2">
                    <Calendar size={16} className="text-cyan-600 dark:text-cyan-400" />
                    <span className="text-xs font-black text-slate-800 dark:text-white">
                      Jadwal Akses Tugas & Tenggat Waktu
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Start Time */}
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-600 dark:text-slate-300">
                        Waktu Mulai Akses
                      </label>
                      <input
                        type="datetime-local"
                        value={startTime}
                        onChange={e => setStartTime(e.target.value)}
                        className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white"
                      />
                    </div>

                    {/* Due Date */}
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-600 dark:text-slate-300">
                        Tenggat Selesai (Due Date)
                      </label>
                      <input
                        type="datetime-local"
                        value={dueDate}
                        onChange={e => setDueDate(e.target.value)}
                        className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Time Limit per Question */}
                <div className="space-y-3 p-4 bg-amber-50/50 dark:bg-amber-950/20 rounded-2xl border border-amber-200/70 dark:border-amber-900/40">
                  <div className="flex items-center justify-between border-b border-amber-200/60 dark:border-amber-900/50 pb-2">
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-amber-600 dark:text-amber-400" />
                      <div>
                        <span className="text-xs font-black text-slate-800 dark:text-white block">
                          Waktu Pengerjaan per Soal (Timer)
                        </span>
                        <span className="text-[11px] text-slate-500">
                          Batasan waktu per nomor soal untuk siswa
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Pilihan Batasan Waktu
                      </label>
                      <select
                        value={timeLimitOption}
                        onChange={e => setTimeLimitOption(e.target.value)}
                        className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white"
                      >
                        <option value="0">Tanpa Batasan Waktu (Bebas)</option>
                        <option value="30">30 Detik / Soal</option>
                        <option value="60">60 Detik / Soal (1 Menit) - Direkomendasikan</option>
                        <option value="90">90 Detik / Soal (1,5 Menit)</option>
                        <option value="120">120 Detik / Soal (2 Menit)</option>
                        <option value="180">180 Detik / Soal (3 Menit)</option>
                        <option value="300">300 Detik / Soal (5 Menit)</option>
                        <option value="custom">Kustom (Masukkan Detik...)</option>
                      </select>
                    </div>

                    {timeLimitOption === 'custom' ? (
                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                          Durasi Kustom (Detik)
                        </label>
                        <input
                          type="number"
                          min="5"
                          max="3600"
                          value={customSeconds}
                          onChange={e => setCustomSeconds(Number(e.target.value))}
                          className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white"
                          placeholder="Contoh: 45"
                        />
                      </div>
                    ) : (
                      <div className="space-y-1 flex flex-col justify-end">
                        <div className="p-2.5 bg-white/80 dark:bg-slate-900/80 rounded-xl border border-amber-200/50 dark:border-amber-900/30 text-[11px] font-bold text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                          <Sparkles size={14} className="text-amber-500 shrink-0" />
                          <span>
                            {timeLimitOption === '0' 
                              ? 'Siswa dapat mengerjakan tanpa batas detik per nomor.'
                              : `Total Estimasi: ~${Math.round((Number(timeLimitOption) * selectedQuestions.length) / 60)} Menit pengerjaan.`
                            }
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Summative Toggle */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-2xl border border-slate-200 dark:border-slate-700 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-bold text-slate-800 dark:text-white">
                      Tandai Sebagai Penilaian Sumatif Bab
                    </p>
                    <p className="text-[11px] text-slate-500">
                      Nilai hasil pengerjaan akan direkap ke dalam Laporan Sumatif Rapor.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={isSummative}
                    onChange={e => setIsSummative(e.target.checked)}
                    className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                </div>

                {/* Questions Summary */}
                <div className="p-4 bg-indigo-50/50 dark:bg-indigo-950/20 rounded-2xl border border-indigo-100 dark:border-indigo-900/30 text-xs text-indigo-900 dark:text-indigo-200 space-y-1">
                  <p className="font-black">
                    📋 Ringkasan Paket Soal ({selectedQuestions.length} Soal PG):
                  </p>
                  <p>
                    Setiap soal akan diberi bobot ~{Math.round(100 / selectedQuestions.length)} poin untuk skor total maksimal 100.
                  </p>
                </div>
              </div>

              {/* Modal Fixed Footer Actions */}
              <div className="p-4 sm:px-6 bg-slate-50/90 dark:bg-slate-900/90 backdrop-blur border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsPublishModalOpen(false)}
                  className="px-5 py-2.5 bg-white dark:bg-slate-800 hover:bg-slate-100 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-2xl text-xs font-bold transition-all"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-2xl text-xs transition-all shadow-lg shadow-indigo-500/20 flex items-center gap-2"
                >
                  <Send size={15} />
                  <span>Publikasikan Tugas Sekarang</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialGeneratorView;
